import { showToastMessage, truncateNumber } from "../../../utils/commonFunctions";
import { CatalogConstants, DEFAULT_POWER_SINK_DATA, DEFAULT_POWER_SINK_FIELDS, EDGE_TYPE_SPECIFIC_FIELDS, PAYLOAD_TYPE_SPECIFIC_FIELDS, POWER_SINK_COMPONENTS } from "./constants";
import _, { isEmpty, mapValues } from 'lodash';

export const getSelectedValue = (value: string | null) => {
    if (value) return { label: value, value: value };
    return null
}

export const getPayloadTypeSpecificFields = (payloadType: string, payloadSubType: string, payloadDetails: string) => {
    return [...(PAYLOAD_TYPE_SPECIFIC_FIELDS[payloadType as string || ''] || []), ...(PAYLOAD_TYPE_SPECIFIC_FIELDS[payloadSubType as string || ''] || []), ...(PAYLOAD_TYPE_SPECIFIC_FIELDS[payloadDetails as string || ''] || [])]
}

export const checkNumberInputValidation = (value: string, max_length: number) => {
    if (!isNaN(Number(value)) && value.length <= max_length) {
        return true;
    } else if (value.length > max_length) {
        showToastMessage('info', `Maximum ${max_length} digits allowed`);
        return false
    }
    return false;
}

export const isFormValid = (formData: { [key: string]: any }, componentType: string) => {
    if (!formData?.name.trim()) return false;
    if (!formData?.peakPower.trim()) return false;
    if (!formData?.averagePower.trim()) return false;
    if (!formData?.mass.trim()) return false;
    if (!formData?.size.trim()) return false;
    if (componentType !== CatalogConstants.BusComponents) {
        if ((formData?.interfaces || []).length === 0) return false;
    }
    if (componentType === CatalogConstants.Payload) {
        if (!formData?.payloadType) return false;
        if (!formData?.payloadSubType) return false;
    }
    if (componentType === CatalogConstants.BusComponents) {
        if (!formData?.catalogueType) return false;
    }
    return true;
};

export const hasNonEmptyCloneTemplateData = (CloneTemplate: any, components: string[]) => {
    const keysToCheck = {
        Comm: [
            'S-Band Antenna',
            // 'X-Band Antenna',
            // 'K-Band Antenna',
            'UHF Antenna',
            'S-Band Radio',
            // 'X-Band Radio',
            // 'K-Band Radio',
            'UHF Radio'
        ],
        Core: [
            'Bus OBC',
            'Bus OS',
            'iADCS',
            'GPS',
            'Thruster',
        ],
        Payload: ['Payload OBC', 'Payload SSD'],
        Power: ['EPS', 'Battery Pack', 'Solar Panel'],
    };

    let validate = true
    components.forEach((component) => {
        keysToCheck[component].forEach((key) => {
            if (!CloneTemplate?.[component]?.[key] || isEmpty(CloneTemplate?.[component]?.[key])) {
                const updatedKey = key === 'iADCS' ? 'ADCS' : key
                showToastMessage('info', `Please select ${updatedKey}`)
                validate = false
            }
        })
    })
    return validate
}

export const getSubSystemByCatalogType = (catalog: string) => {
    if (['Payload OBC', 'Payload SSD'].includes(catalog)) {
        return 'Payload Server';
    } else if (['S-Band Antenna', 'X-Band Antenna', 'K-Band Antenna', 'UHF Antenna', 'S-Band Radio', 'X-Band Radio', 'K-Band Radio', 'UHF Radio'].includes(catalog)) {
        return "Communication";
    } else if (['EPS', 'Battery Pack', 'Solar Panel'].includes(catalog)) {
        return "Power";
    } else {
        return 'Core'
    }
}

export const validateBusForm = (formData: { [key: string]: any }) => {
    Object.entries(formData?.['inertia']).forEach(([key, value]) => {
        if (value === '' || Number(formData?.size?.['breadth']) < 0) {
            showToastMessage('info', isEmpty(value) ? `Please enter ${key}` : `${key} cannot be negative`)
            return false
        }
    })

    if (!formData?.['name'].trim()) {
        showToastMessage('info', 'Please enter name')
        return false
    } if (isEmpty(formData?.size?.['breadth']) || Number(formData?.size?.['breadth']) <= 0) {
        showToastMessage('info', isEmpty(formData?.size?.['breadth']) ? 'Please enter breadth' : 'Breadth must be greater than 0')
        return false
    } if (isEmpty(formData?.size?.['height']) || Number(formData?.size?.['height']) <= 0) {
        showToastMessage('info', isEmpty(formData?.size?.['height']) ? 'Please enter height' : 'Height must be greater than 0')
        return false
    } if (isEmpty(formData?.size?.['width']) || Number(formData?.size?.['width']) <= 0) {
        showToastMessage('info', isEmpty(formData?.size?.['width']) ? 'Please enter width' : 'Width must be greater than 0')
        return false
    } if (isEmpty(formData?.['maxPower']) || Number(formData?.['maxPower']) <= 0) {
        showToastMessage('info', isEmpty(formData?.['maxPower']) ? 'Please enter max power' : 'Max power must be greater than 0')
        return false
    } if (isEmpty(formData?.['maxMass']) || Number(formData?.['maxMass']) <= 0) {
        showToastMessage('info', isEmpty(formData?.['maxMass']) ? 'Please enter max mass' : 'Max mass must be greater than 0')
        return false
    } if (isEmpty(formData?.['maxPayloadSize']) || Number(formData?.['maxPayloadSize']) <= 0) {
        showToastMessage('info', isEmpty(formData?.['maxPayloadSize']) ? 'Please enter max payload size' : 'Max payload size must be greater than 0')
        return false
    } if (isEmpty(formData?.['maxPayloadPower']) || Number(formData?.['maxPayloadPower']) <= 0) {
        showToastMessage('info', isEmpty(formData?.['maxPayloadPower']) ? 'Please enter max payload power' : 'Max payload power must be greater than 0')
        return false
    } if (isEmpty(formData?.['maxPayloadMass']) || Number(formData?.['maxPayloadMass']) <= 0) {
        showToastMessage('info', isEmpty(formData?.['maxPayloadMass']) ? 'Please enter max payload mass' : 'Max payload mass must be greater than 0')
        return false
    }
    return true
}

const validatePowerSinksData = (formData: { [key: string]: any }) => {
    const power_sinks = formData?.['power_sinks'] || []
    let validate = true
    power_sinks.forEach((power_sink: any, index: number) => {
        Object.entries(power_sink).some(([power_sink_key, power_sink_value]) => {
            if (!['quantity', 'isContinuousLoad'].includes(power_sink_key) && (_.isEmpty(power_sink_key) || _.isEmpty(power_sink_value))) {
                const updated_key = DEFAULT_POWER_SINK_FIELDS.find((field) => field?.key === power_sink_key)?.placeholder
                const message = `Power Sink ${index + 1} must have a ${updated_key} value`;
                showToastMessage('info', message);
                validate = false
            }
        })
    })
    return validate
}

export const validateForm = (component: string, formData: { [key: string]: any }, additionalConfigurations: { [key: string]: string }[]) => {
    const ALL_TYPE_SPECIFIC_FIELDS = (component === CatalogConstants.Payload ? [...(PAYLOAD_TYPE_SPECIFIC_FIELDS[formData.payloadSubType as string || ''] || []), ...(PAYLOAD_TYPE_SPECIFIC_FIELDS[formData.payloadDetails as string || ''] || [])] : component === CatalogConstants.Edge ? EDGE_TYPE_SPECIFIC_FIELDS : []).map(field => field.name);
    let validate = true
    if (Number(formData?.peakPower) <= 0) {
        showToastMessage('info', 'Peak power consumption must be greater than 0 (W)')
        validate = false
    } if (Number(formData?.avgPower) <= 0 || Number(formData?.avgPower) > Number(formData?.peakPower)) {
        showToastMessage('info', 'Average power consumption must be greater than 0 (W) and less than or equal to peak power consumption')
        validate = false
    } if (Number(formData?.mass) <= 0) {
        showToastMessage('info', 'Mass must be greater than 0 (kg)')
        validate = false
    } if (Number(formData?.size) <= 0) {
        showToastMessage('info', 'Size must be greater than 0 (U)')
        validate = false
    } if (CatalogConstants.Payload === component) {
        if (!formData?.payloadType) {
            showToastMessage('info', 'Please select Payload Type')
            validate = false
        } if (!formData?.payloadSubType) {
            showToastMessage('info', 'Please select Payload Subtype')
            validate = false
        } if (!formData?.voltage || Number(formData?.voltage) <= 0) {
            showToastMessage('info', 'Voltage must be greater than 0 (V)')
            validate = false
        } if (!validatePowerSinksData(formData)) {
            validate = false
        }
    } if (CatalogConstants.Edge === component) {
        if (!validatePowerSinksData(formData)) {
            validate = false
        }
    } if (CatalogConstants.BusComponents === component) {
        if (!formData?.catalogueType) {
            showToastMessage('info', 'Please select Catalog Type')
            validate = false
        } if (formData?.catalogueType === 'Payload SSD' && (isEmpty(formData?.['size_in_MB']) || Number(formData?.['size_in_MB']) <= 0)) {
            showToastMessage('info', isEmpty(formData?.['size_in_MB']) ? 'Memory size is required' : 'Memory size must be greater than 0 (GB)')
            validate = false
        } if (POWER_SINK_COMPONENTS.includes(formData?.catalogueType) && formData?.catalogueType !== 'iADCS' && !validatePowerSinksData(formData)) {
            validate = false
        } if (formData?.catalogueType === 'iADCS' && formData?.component_specific_attributes?.Components?.length > 0) {
            const components = formData?.component_specific_attributes?.Components;
            components.some((item: any) =>
                Object.entries(item).some(([key, value]) => {
                    if (key === 'power_sink') {
                        const power_sink = value || {};
                        Object.entries(power_sink).some(([power_sink_key, power_sink_value]) => {
                            if (!['quantity', 'isContinuousLoad'].includes(power_sink_key) && (_.isEmpty(power_sink_key) || _.isEmpty(power_sink_value))) {
                                const updated_key = DEFAULT_POWER_SINK_FIELDS.find((field) => field?.key === power_sink_key)?.placeholder
                                const message = `Power Sink must have a ${updated_key} value`;
                                showToastMessage('info', message);
                                validate = false
                            }
                        })
                    }
                    else if (_.isEmpty(key) || _.isEmpty(value)) {
                        const updated_key = key === 'friendly-name' ? 'Component name' : key === 'numUnits' ? 'Number of units' : 'Property value'
                        const message = _.isEmpty(key) ? 'Property name cannot be empty' : `${updated_key} cannot be empty`;
                        showToastMessage('info', message);
                        validate = false
                    }
                })
            );
        } if (!additionalConfigurations?.length) {
            showToastMessage('info', 'Please add specifications')
            validate = false
        }
    } if (additionalConfigurations?.length > 0) {
        additionalConfigurations.some(object => Object.entries(object).some(([key, value]) => {
            if (_.isEmpty(key) || _.isEmpty(value)) {
                const message = _.isEmpty(key) ? 'Property name cannot be empty' : `Property value cannot be empty`;
                showToastMessage('info', message);
                validate = false
            }
        }));
    } if (formData?.typeSpecificParams && typeof formData.typeSpecificParams === 'object') {
        if (ALL_TYPE_SPECIFIC_FIELDS?.length > 0) {
            for (const field of ALL_TYPE_SPECIFIC_FIELDS) {
                const value = formData.typeSpecificParams[field];
                if (_.isEmpty(value)) {
                    showToastMessage('info', Array.isArray(value) ? `At least one ${field} must be selected` : `${field} cannot be empty`);
                    validate = false;
                }
            }
        }
    }
    return validate;
}

const getUpdatedTypeSpecificParams = (formData: { [key: string]: any }, component: string) => {
    if (component === CatalogConstants.Payload) {
        if (formData?.payloadDetails === 'Multispectral') {
            return {
                'GSD': Number(formData?.typeSpecificParams?.GSD),
                'Swath': Number(formData?.typeSpecificParams?.Swath),
                'Front Aperture': `~ ${formData?.typeSpecificParams?.['Front Aperture']} cm`,
                'Spectral Bands': `${formData?.typeSpecificParams?.['Spectral Bands']} Bands MS`
            }
        } else if (formData?.payloadDetails === 'SAR') {
            return {
                'Frequency': `${formData?.typeSpecificParams?.Frequency} GHz`,
                'Antenna Size': `${formData?.typeSpecificParams?.['Antenna Size']} m`,
                'Pulse Width': `${formData?.typeSpecificParams?.['Pulse Width']} μs`,
                'Sampling Rate': `${formData?.typeSpecificParams?.['Sampling Rate']} MHz`
            }
        } else if (formData?.payloadSubType === 'RF') {
            return {
                'Frequency Bands': formData?.typeSpecificParams?.['Frequency Bands'],
                'Data Rate': Number(formData?.typeSpecificParams?.['Data Rate']),
                'Number of Beams': Number(formData?.typeSpecificParams?.['Number of Beams'])
            }
        } else {
            return formData?.typeSpecificParams
        }
    } else if (component === CatalogConstants.Edge) {
        return {
            'CPU Cores': `${formData?.typeSpecificParams?.['CPU Cores']}`,
            'GPU': `${formData?.typeSpecificParams?.GPU}`,
            'Memory': `${formData?.typeSpecificParams?.Memory} GB`,
            'Memory Type': `${formData?.typeSpecificParams?.['Memory Type']}`,
            'Storage': `${formData?.typeSpecificParams?.Storage} GB Flash`,
            'Performance': `${formData?.typeSpecificParams?.Performance} TOPs`,
            'Cache': `${formData?.typeSpecificParams?.Cache} MB`,
            'Tensor Cores': `${formData?.typeSpecificParams?.['Tensor Cores']}`,
        }
    }
}

const getRevertedTypeSpecificParams = (params: { [key: string]: any }, component: string) => {
    if (component === CatalogConstants.Payload) {
        if (params['Spectral Bands']) {
            // Multispectral
            return {
                GSD: String(params['GSD'] || ''),
                Swath: String(params['Swath'] || ''),
                'Front Aperture': params['Front Aperture']?.replace(/~\s?|\scm/g, '').trim() || '',
                'Spectral Bands': params['Spectral Bands']?.replace(/\sBands MS/g, '').trim() || ''
            }
        } else if (params['Antenna Size']) {
            // SAR
            return {
                Frequency: params['Frequency']?.replace(/\sGHz/g, '').trim() || '',
                'Antenna Size': params['Antenna Size']?.replace(/\sm/g, '').trim() || '',
                'Pulse Width': params['Pulse Width']?.replace(/\sμs/g, '').trim() || '',
                'Sampling Rate': params['Sampling Rate']?.replace(/\sMHz/g, '').trim() || ''
            }
        } else if (params['Frequency Bands']) {
            // RF
            return {
                'Frequency Bands': params['Frequency Bands'] || '',
                'Data Rate': String(params['Data Rate'] || ''),
                'Number of Beams': String(params['Number of Beams'] || '')
            }
        } else {
            return params;
        }
    } else if (component === CatalogConstants.Edge) {
        return {
            'CPU Cores': params['CPU Cores'] || '',
            GPU: params['GPU'] || '',
            Memory: params['Memory']?.replace(/\sGB/g, '').trim() || '',
            'Memory Type': params['Memory Type'] || '',
            Storage: params['Storage']?.replace(/\sGB Flash/g, '').trim() || '',
            Performance: params['Performance']?.replace(/\sTOPs/g, '').trim() || '',
            Cache: params['Cache']?.replace(/\sMB/g, '').trim() || '',
            'Tensor Cores': params['Tensor Cores'] || '',
        }
    }
};

const getUpdatedPowerSinkData = (type: string, power_sinks: any[]) => {
    const updated_data: any[] = []
    power_sinks.forEach((power_sink: any) => {
        const data: any = {}
        Object.entries(power_sink).forEach(([power_sink_key, power_sink_value]) => {
            if (['idle_power', 'avg_power', 'peak_power'].includes(power_sink_key)) {
                data[power_sink_key] = type === 'number' ? Number(power_sink_value) : String(power_sink_value)
            } else {
                data[power_sink_key] = power_sink_value
            }
        })
        updated_data.push(data)
    })
    return updated_data
}
export const getUpdatedFormData = (additionalConfigurations, formData: { [key: string]: any }, component: string) => {
    const updatedComponentName = formData.name.trim();
    let additionalConfig = Object.assign({}, ...additionalConfigurations)
    let catalogueType = "";
    let subsystemType = "";
    if (component === CatalogConstants.Payload) {
        catalogueType = "Payload";
        subsystemType = "Payload Server";
    } else if (component === CatalogConstants.Edge) {
        catalogueType = "Edge Node";
        subsystemType = "Edge";
    } else if (component === CatalogConstants.BusComponents) {
        catalogueType = formData?.catalogueType;
        subsystemType = getSubSystemByCatalogType(formData?.catalogueType);
    }
    if (component === CatalogConstants.Payload) {
        const component_specific_data = {
            interfaces: formData?.interfaces || [],
            payloadDetails: formData?.payloadDetails === 'Multispectral' ? 'MX / Multispectral' : formData?.payloadDetails || '',
            payloadSubType: formData?.payloadSubType || '',
            payloadType: formData?.payloadType || '',
            typeSpecificParams: getUpdatedTypeSpecificParams(formData, component) || {},
            additionalParams: additionalConfig,
        }
        return {
            "catalogue-type": catalogueType,
            component_specific_attributes: component_specific_data,
            description: updatedComponentName,
            max_power_consumption_in_watts: formData?.peakPower,
            average_power_consumption: parseFloat(formData?.averagePower),
            name: updatedComponentName,
            voltage: parseFloat(formData?.voltage),
            power_sinks: getUpdatedPowerSinkData('number', formData?.power_sinks),
            "product-cookie": {
                Size: parseFloat(formData?.size),
                "Supported Interfaces": (formData?.interfaces || []).join(' | '),
                Weight: formData?.mass + ' kg',
                "friendly-name": updatedComponentName,
            },
            "product-flavor": updatedComponentName,
            "product-line": "",
            "subsystem-type": subsystemType,
            visibility: "public",
            weight_in_gms: `${(+formData?.mass * 1000)}`,
            "edge-processing": false
        }
    } else if (component === CatalogConstants.Edge) {
        return {
            "catalogue-type": catalogueType,
            component_specific_attributes: {
                interfaces: formData?.interfaces || [],
                additionalParams: additionalConfig,
            },
            description: updatedComponentName,
            max_power_consumption_in_watts: formData?.peakPower,
            average_power_consumption: parseFloat(formData?.averagePower),
            name: updatedComponentName,
            power_sinks: getUpdatedPowerSinkData('number', formData?.power_sinks),
            "product-cookie": {
                Size: formData?.size,
                'Peak Power': `${formData?.peakPower} W`,
                "friendly-name": updatedComponentName,
                ...getUpdatedTypeSpecificParams(formData, component)
            },
            "product-flavor": updatedComponentName,
            "product-line": '',
            "subsystem-type": subsystemType,
            visibility: "public",
            weight_in_gms: `${(+formData?.mass * 1000)}`,
            "edge-processing": false
        }
    } else {
        const updated_component_specific_attributes = formData?.component_specific_attributes?.Components ? {
            Components: [...formData?.component_specific_attributes?.Components].map(obj =>
                _.omitBy(obj, (value, key) => _.isEmpty(key))
            )
        } : formData?.component_specific_attributes

        const data = {
            "catalogue-type": catalogueType,
            component_specific_attributes: updated_component_specific_attributes,
            description: updatedComponentName,
            max_power_consumption_in_watts: formData?.peakPower,
            average_power_consumption: parseFloat(formData?.averagePower),
            name: updatedComponentName,
            "product-cookie": {
                Size: parseFloat(formData?.size),
                ...additionalConfig
            },
            "product-flavor": updatedComponentName,
            "product-line": '',
            "subsystem-type": subsystemType,
            visibility: "public",
            weight_in_gms: `${(+formData?.mass * 1000)}`,
            "edge-processing": false
        }
        if (catalogueType === 'Payload SSD') {
            data['size_in_MB'] = Number(formData?.['size_in_MB']) * 1024
        }
        if (POWER_SINK_COMPONENTS.includes(catalogueType) && catalogueType !== 'iADCS') {
            data['power_sinks'] = getUpdatedPowerSinkData('number', formData?.power_sinks)
        }
        return data
    }
}

export const getNewBusData = (blankTemplateData: any, CloneTemplate: any, busData: any, userData: any) => {
    const default_temp = blankTemplateData;
    const updatedAssembly: any[] = [
        {
            'communication': {
                ...default_temp?.['assembly']?.[0]?.['communication'],
                'antennas': {
                    ...default_temp?.['assembly']?.[0]?.['communication']?.['antennas'],
                    'S-band': {
                        'atmos-data': {
                            ...default_temp?.['assembly']?.[0]?.['communication']?.['antennas']?.['S-band']?.['atmos-data'],
                            ...CloneTemplate['Comm']?.['S-Band Antenna'] ?? default_temp?.['assembly']?.[0]?.['communication']?.['antennas']?.['S-band']?.['atmos-data']
                        },
                        'user_data': {
                            ...default_temp?.['assembly']?.[0]?.['communication']?.['antennas']?.['S-band']?.['user_data'],
                            'numUnits': CloneTemplate?.['Comm']?.['S-Band Antenna'] ? 1 : default_temp?.['assembly']?.[0]?.['communication']?.['antennas']?.['S-band']?.['user_data']?.['numUnits'],
                            'userOption': default_temp?.['assembly']?.[0]?.['communication']?.['antennas']?.['S-band']?.['user_data']?.['userOption'],
                        }
                    },
                    'X-band': {
                        'atmos-data': {
                            ...default_temp?.['assembly']?.[0]?.['communication']?.['antennas']?.['X-band']?.['atmos-data'],
                            ...CloneTemplate['Comm']?.['X-Band Antenna'] ?? default_temp?.['assembly']?.[0]?.['communication']?.['antennas']?.['X-band']?.['atmos-data']
                        },
                        'user_data': {
                            ...default_temp?.['assembly']?.[0]?.['communication']?.['antennas']?.['X-band']?.['user_data'],
                            'numUnits': CloneTemplate?.['Comm']?.['X-Band Antenna'] ? 1 : default_temp?.['assembly']?.[0]?.['communication']?.['antennas']?.['X-band']?.['user_data']?.['numUnits'],
                            'userOption': default_temp?.['assembly']?.[0]?.['communication']?.['antennas']?.['X-band']?.['user_data']?.['userOption'],
                        }
                    },
                    'K-band': {
                        'atmos-data': {
                            ...default_temp?.['assembly']?.[0]?.['communication']?.['antennas']?.['K-band']?.['atmos-data'],
                            ...CloneTemplate['Comm']?.['K-Band Antenna'] ?? default_temp?.['assembly']?.[0]?.['communication']?.['antennas']?.['K-band']?.['atmos-data']
                        },
                        'user_data': {
                            ...default_temp?.['assembly']?.[0]?.['communication']?.['antennas']?.['K-band']?.['user_data'],
                            'numUnits': CloneTemplate?.['Comm']?.['K-Band Antenna'] ? 1 : default_temp?.['assembly']?.[0]?.['communication']?.['antennas']?.['K-band']?.['user_data']?.['numUnits'],
                            'userOption': default_temp?.['assembly']?.[0]?.['communication']?.['antennas']?.['K-band']?.['user_data']?.['userOption'],
                        }
                    },
                    'UHF': {
                        'atmos-data': default_temp?.['assembly']?.[0]?.['communication']?.['antennas']?.['UHF']?.['atmos-data'] ? {
                            ...default_temp?.['assembly']?.[0]?.['communication']?.['antennas']?.['UHF']?.['atmos-data'],
                            ...CloneTemplate['Comm']?.['UHF Antenna']
                        } : CloneTemplate['Comm']?.['UHF Antenna'] || {},
                        'user_data': {
                            ...default_temp?.['assembly']?.[0]?.['communication']?.['antennas']?.['UHF']?.['user_data'],
                            'numUnits': CloneTemplate?.['Comm']?.['UHF Antenna'] ? 1 : default_temp?.['assembly']?.[0]?.['communication']?.['antennas']?.['UHF']?.['user_data']?.['numUnits'] || 0,
                            'userOption': default_temp?.['assembly']?.[0]?.['communication']?.['antennas']?.['UHF']?.['user_data']?.['userOption'] || '',
                        }
                    },
                },
                'comm-devices': {
                    ...default_temp?.['assembly']?.[0]?.['communication']?.['comm-devices'],
                    'S-band': {
                        'atmos-data': {
                            ...default_temp?.['assembly']?.[0]?.['communication']?.['comm-devices']?.['S-band']?.['atmos-data'],
                            ...CloneTemplate['Comm']?.['S-Band Radio'] ?? default_temp?.['assembly']?.[0]?.['communication']?.['comm-devices']?.['S-band']?.['atmos-data']
                        },
                        'user_data': {
                            ...default_temp?.['assembly']?.[0]?.['communication']?.['comm-devices']?.['S-band']?.['user_data'],
                            'numUnits': CloneTemplate?.['Comm']?.['S-Band Radio'] ? 1 : default_temp?.['assembly']?.[0]?.['communication']?.['comm-devices']?.['S-band']?.['user_data']?.['numUnits'],
                            'userOption': default_temp?.['assembly']?.[0]?.['communication']?.['comm-devices']?.['S-band']?.['user_data']?.['userOption'],
                        }
                    },
                    'X-band': {
                        'atmos-data': {
                            ...default_temp?.['assembly']?.[0]?.['communication']?.['comm-devices']?.['X-band']?.['atmos-data'],
                            ...CloneTemplate['Comm']?.['X-Band Radio'] ?? default_temp?.['assembly']?.[0]?.['communication']?.['comm-devices']?.['X-band']?.['atmos-data']
                        },
                        'user_data': {
                            ...default_temp?.['assembly']?.[0]?.['communication']?.['comm-devices']?.['X-band']?.['user_data'],
                            'numUnits': CloneTemplate?.['Comm']?.['X-Band Radio'] ? 1 : default_temp?.['assembly']?.[0]?.['communication']?.['comm-devices']?.['X-band']?.['user_data']?.['numUnits'],
                            'userOption': default_temp?.['assembly']?.[0]?.['communication']?.['comm-devices']?.['X-band']?.['user_data']?.['userOption'],
                        }
                    },
                    'K-band': {
                        'atmos-data': {
                            ...default_temp?.['assembly']?.[0]?.['communication']?.['comm-devices']?.['K-band']?.['atmos-data'],
                            ...CloneTemplate['Comm']?.['K-Band Radio'] ?? default_temp?.['assembly']?.[0]?.['communication']?.['comm-devices']?.['K-band']?.['atmos-data']
                        },
                        'user_data': {
                            ...default_temp?.['assembly']?.[0]?.['communication']?.['comm-devices']?.['K-band']?.['user_data'],
                            'numUnits': CloneTemplate?.['Comm']?.['K-Band Radio'] ? 1 : default_temp?.['assembly']?.[0]?.['communication']?.['comm-devices']?.['K-band']?.['user_data']?.['numUnits'],
                            'userOption': default_temp?.['assembly']?.[0]?.['communication']?.['comm-devices']?.['K-band']?.['user_data']?.['userOption'],
                        }
                    },
                    'UHF': {
                        'atmos-data': {
                            ...default_temp?.['assembly']?.[0]?.['communication']?.['comm-devices']?.['UHF']?.['atmos-data'],
                            ...CloneTemplate['Comm']?.['UHF Radio'] ?? default_temp?.['assembly']?.[0]?.['communication']?.['comm-devices']?.['UHF']?.['atmos-data']
                        },
                        'user_data': {
                            ...default_temp?.['assembly']?.[0]?.['communication']?.['comm-devices']?.['UHF']?.['user_data'],
                            'numUnits': CloneTemplate?.['Comm']?.['UHF Radio'] ? 1 : default_temp?.['assembly']?.[0]?.['communication']?.['comm-devices']?.['UHF']?.['user_data']?.['numUnits'],
                            'userOption': default_temp?.['assembly']?.[0]?.['communication']?.['comm-devices']?.['UHF']?.['user_data']?.['userOption'],
                        }
                    },
                }
            },
            'core': {
                ...default_temp?.['assembly']?.[0]?.['core'],
                'ADCS': {
                    'Horizon Sensor': {
                        'atmos-data': default_temp?.['assembly']?.[0]?.['core']?.['ADCS']?.['Horizon Sensor']?.['atmos-data'],
                        'user_data': default_temp?.['assembly'][0]['core']['ADCS']['Horizon Sensor']['user_data']
                    },
                    'Star Sensor': {
                        'atmos-data': default_temp?.['assembly']?.[0]?.['core']?.['ADCS']?.['Star Sensor']?.['atmos-data'],
                        'user_data': default_temp?.['assembly'][0]['core']['ADCS']['Star Sensor']['user_data']
                    },
                    'Sun-n-Nadir Sensor': {
                        'atmos-data': default_temp?.['assembly']?.[0]?.['core']?.['ADCS']?.['Sun-n-Nadir Sensor']?.['atmos-data'],
                        'user_data': default_temp?.['assembly'][0]['core']['ADCS']['Sun-n-Nadir Sensor']['user_data']
                    },
                    'Torquer': {
                        'atmos-data': default_temp?.['assembly']?.[0]?.['core']?.['ADCS']?.['Torquer']?.['atmos-data'],
                        'user_data': default_temp?.['assembly'][0]['core']['ADCS']['Torquer']['user_data']
                    },
                    'Wheels': {
                        'atmos-data': default_temp?.['assembly']?.[0]?.['core']?.['ADCS']?.['Wheels']?.['atmos-data'],
                        'user_data': default_temp?.['assembly'][0]['core']['ADCS']['Wheels']['user_data']
                    },
                },
                'ADCS Software': default_temp?.['assembly'][0]['core']['ADCS Software'] || '',
                'Bus OBC': {
                    'atmos-data': {
                        ...default_temp?.['assembly']?.[0]?.['core']?.['Bus OBC']?.['atmos-data'],
                        ...CloneTemplate?.['Core']?.['Bus OBC'] ?? default_temp?.['assembly']?.[0]?.['core']?.['Bus OBC']?.['atmos-data']
                    },
                    'user_data': {
                        ...default_temp?.['assembly']?.[0]?.['core']?.['Bus OBC']?.['user_data'],
                        'numUnits': CloneTemplate?.['Core']?.['Bus OBC'] ? 1 : default_temp?.['assembly']?.[0]?.['core']?.['Bus OBC']?.['user_data']?.['numUnits'],
                        'userOption': default_temp?.['assembly'][0]['core']['Bus OBC']['user_data']?.['userOption']
                    }
                },
                'Bus OS': {
                    'atmos-data': {
                        ...default_temp?.['assembly']?.[0]?.['core']?.['Bus OS']?.['atmos-data'],
                        ...CloneTemplate?.['Core']?.['Bus OS'] ?? default_temp?.['assembly']?.[0]?.['core']?.['Bus OS']?.['atmos-data']
                    },
                    'user_data': {
                        ...default_temp?.['assembly']?.[0]?.['core']?.['Bus OS']?.['user_data'],
                        'numUnits': CloneTemplate?.['Core']?.['Bus OS'] ? 1 : default_temp?.['assembly']?.[0]?.['core']?.['Bus OS']?.['user_data']?.['numUnits'],
                        'userOption': default_temp?.['assembly'][0]['core']['Bus OS']['user_data']?.['userOption']
                    }
                },
                'GPS': {
                    'atmos-data': {
                        ...default_temp?.['assembly']?.[0]?.['core']?.['GPS']?.['atmos-data'],
                        ...CloneTemplate?.['Core']?.['GPS'] ?? default_temp?.['assembly']?.[0]?.['core']?.['GPS']?.['atmos-data']
                    },
                    'user_data': {
                        ...default_temp?.['assembly']?.[0]?.['core']?.['GPS']?.['user_data'],
                        'numUnits': CloneTemplate?.['Core']?.['GPS'] ? 1 : default_temp?.['assembly']?.[0]?.['core']?.['GPS']?.['user_data']?.['numUnits'],
                        'userOption': default_temp?.['assembly'][0]['core']['GPS']['user_data']?.['userOption']
                    }
                },
                'Temperature Sensor': {
                    'atmos-data': {
                        ...default_temp?.['assembly']?.[0]?.['core']?.['Temperature Sensor']?.['atmos-data'],
                        ...CloneTemplate?.['Core']?.['Temperature Sensor'] ?? default_temp?.['assembly']?.[0]?.['core']?.['Temperature Sensor']?.['atmos-data']
                    },
                    'user_data': {
                        ...default_temp?.['assembly']?.[0]?.['core']?.['Temperature Sensor']?.['user_data'],
                        'numUnits': CloneTemplate?.['Core']?.['Temperature SensorCount'] ? CloneTemplate?.['Core']['Temperature SensorCount'] : default_temp?.['assembly']?.[0]?.['core']?.['Temperature Sensor']?.['user_data']?.['numUnits'],
                        'userOption': default_temp?.['assembly'][0]['core']['Temperature Sensor']['user_data']?.['userOption']
                    }
                },

                'Thruster': {
                    'atmos-data': {
                        ...default_temp?.['assembly']?.[0]?.['core']?.['Thruster']?.['atmos-data'],
                        ...CloneTemplate?.['Core']?.['Thruster'] ?? default_temp?.['assembly']?.[0]?.['core']?.['Thruster']?.['atmos-data']
                    },
                    'user_data': {
                        ...default_temp?.['assembly']?.[0]?.['core']?.['Thruster']?.['user_data'],
                        'numUnits': CloneTemplate?.['Core']?.['ThrusterCount'] ? CloneTemplate?.['Core']['ThrusterCount'] : default_temp?.['assembly']?.[0]?.['core']?.['Thruster']?.['user_data']?.['numUnits'],
                        'userOption': default_temp?.['assembly'][0]?.['core']?.['Thruster']?.['user_data']?.['userOption']
                    }
                },
            },
            'iADCS': {
                'atmos-data': {
                    ...default_temp?.['assembly']?.[0]?.['iADCS']?.['atmos-data'],
                    ...CloneTemplate?.['Core']['iADCS'],
                    'adcs_config': busData?.simulationParameters ? JSON.parse(busData?.simulationParameters?.data) : default_temp?.['assembly'][0]['iADCS']['atmos-data']['adcs_config'],
                    'inertia': mapValues(busData?.inertia, Number) || default_temp?.['assembly'][0]['iADCS']['atmos-data']['inertia'],
                },
                'user_data': {
                    ...default_temp?.['assembly']?.[0]?.['iADCS']?.['user_data'],
                    'numUnits': CloneTemplate?.['Core']?.['iADCS'] ? 1 : default_temp?.['assembly'][0]['iADCS']['user_data']?.['numUnits'],
                    'userOption': default_temp?.['assembly'][0]['iADCS']['user_data']?.['userOption']
                }
            },
            'payloadServer': {
                ...default_temp?.['assembly']?.[0]?.['payloadServer'],
                'Payload OBC': {
                    'atmos-data': {
                        ...default_temp?.['assembly']?.[0]?.['payloadServer']?.['Payload OBC']?.['atmos-data'],
                        ...CloneTemplate?.['Payload']?.['Payload OBC'] ?? default_temp?.['assembly']?.[0]?.['payloadServer']?.['Payload OBC']?.['atmos-data']
                    },
                    'user_data': {
                        ...default_temp?.['assembly']?.[0]?.['payloadServer']?.['Payload OBC']?.['user_data'],
                        'numUnits': CloneTemplate?.['Payload']?.['Payload OBC'] ? 1 : default_temp?.['assembly']?.[0]?.['payloadServer']?.['Payload OBC']?.['user_data']?.['numUnits'],
                        'userOption': default_temp?.['assembly'][0]['payloadServer']['Payload OBC']['user_data']?.['userOption']
                    }
                },
                'Payload SSD': {
                    'atmos-data': {
                        ...default_temp?.['assembly']?.[0]?.['payloadServer']?.['Payload SSD']?.['atmos-data'],
                        ...CloneTemplate?.['Payload']?.['Payload SSD'] ?? default_temp?.['assembly']?.[0]?.['payloadServer']?.['Payload SSD']?.['atmos-data']
                    },
                    'user_data': {
                        ...default_temp?.['assembly']?.[0]?.['payloadServer']?.['Payload SSD']?.['user_data'],
                        'numUnits': CloneTemplate?.['Payload']?.['Payload SSDCount'] ? CloneTemplate?.['Payload']['Payload SSDCount'] : default_temp?.['assembly']?.[0]?.['payloadServer']?.['Payload SSD']?.['user_data']?.['numUnits'],
                        'userOption': default_temp?.['assembly'][0]['payloadServer']['Payload SSD']['user_data']?.['userOption']
                    }
                },
            },
            'power': {
                ...default_temp?.['assembly']?.[0]?.['power'],
                'Battery Pack': {
                    'atmos-data': {
                        ...default_temp?.['assembly']?.[0]?.['power']?.['Battery Pack']?.['atmos-data'],
                        ...CloneTemplate?.['Power']?.['Battery Pack'] ?? default_temp?.['assembly']?.[0]?.['power']?.['Battery Pack']?.['atmos-data']
                    },
                    'user_data': {
                        ...default_temp?.['assembly']?.[0]?.['power']?.['Battery Pack']?.['user_data'],
                        'numUnits': CloneTemplate?.['Power']?.['Battery Pack'] ? 1 : default_temp?.['assembly']?.[0]?.['power']?.['Battery Pack']?.['user_data']?.['numUnits'],
                        'userOption': default_temp?.['assembly'][0]['power']['Battery Pack']['user_data']?.['userOption']
                    }
                },
                'EPS': {
                    'atmos-data': CloneTemplate?.['Power']?.['EPS'] ? {
                        ...default_temp?.['assembly']?.[0]?.['power']?.['EPS']?.['atmos-data'],
                        ...CloneTemplate?.['Power']['EPS']
                    } : default_temp?.['assembly']?.[0]?.['power']?.['EPS']?.['atmos-data'],
                    'user_data': {
                        ...default_temp?.['assembly'][0]['power']['EPS']['user_data'],
                        'numUnits': CloneTemplate?.['Power']?.['EPS'] ? 1 : default_temp?.['assembly']?.[0]?.['power']?.['EPS']?.['user_data']?.['numUnits'],
                        'userOption': default_temp?.['assembly'][0]['power']['EPS']['user_data']?.['userOption']
                    }
                },
                'MPTT': default_temp?.['assembly'][0]['MPTT'],
                'Solar Panel': {
                    'atmos-data': {
                        ...default_temp?.['assembly']?.[0]?.['power']?.['Solar Panel']?.['atmos-data'],
                        ...CloneTemplate?.['Power']?.['Solar Panel'] ?? default_temp?.['assembly']?.[0]?.['power']?.['Solar Panel']?.['atmos-data']
                    },
                    'user_data': {
                        ...default_temp?.['assembly']?.[0]?.['power']?.['Solar Panel']?.['user_data'],
                        'numUnits': CloneTemplate?.['Power']?.['Solar PanelCount'] ? CloneTemplate?.['Power']['Solar PanelCount'] : default_temp?.['assembly']?.[0]?.['power']?.['Solar Panel']?.['user_data']?.['numUnits'],
                        'userOption': default_temp?.['assembly'][0]['power']['Solar Panel']['user_data']?.['userOption']
                    }
                },
            },
            'version': default_temp?.['assembly']?.[0]?.['version']
        }
    ]

    const { height, width, breadth } = mapValues(busData?.size, Number)
    const maxSize = truncateNumber(((height * width * breadth) / 100), 1)
    const maxPower = Number(busData?.maxPower)
    const maxMass = Number(busData?.maxMass)

    const payloadPower = Number(busData?.maxPayloadPower) || 0
    const payloadMass = Number(busData?.maxPayloadMass) || 0
    const payloadSize = Number(busData?.maxPayloadSize) || 0

    const updatedTemplateAttribute = {
        ...default_temp?.['template-attribute'],
        'ADCS': 'iADCS',
        'IMU': "iIMU",
        'NumSolarPanel': (CloneTemplate?.['Power']?.['Solar PanelCount'] ? CloneTemplate?.['Power']?.['Solar PanelCount'] : default_temp?.['template-attribute']?.['NumSolarPanel']),
        'battery-capacity': maxPower,
        'mass': maxMass,
        'bus-mass': maxMass - payloadMass,
        'bus-obc': 'Antaris Core V1',
        'bus-power': maxPower - payloadPower,
        'bus-size': maxSize - payloadSize,
        'bus-type': `${maxSize}U`,
        'maxWeightInKg': maxMass,
        'propulsion': 'Yes',
        'sizeInUnits': maxSize,
        'sizeInXYZ': `${height}m x ${width}m x ${breadth}m`,
        'type': default_temp?.['template-attribute']?.['type'],
        'isCommandFilterApplicable': false
    }

    const data = {
        assembly: updatedAssembly,
        'build_info': default_temp?.['build_info'],
        'container-project': default_temp?.['container-project'],
        'latest-version-id': default_temp?.['latest-version-id'],
        'name': busData?.name,
        'template-attribute': updatedTemplateAttribute,
        'owner-org': userData?.['owner_org'],
    };
    return data
}

export const getEditedBusFormData = (busData: any) => {
    const [height, width, breadth] = busData?.['template-attribute']?.['sizeInXYZ'].split("x").map(val => parseFloat(val));

    const maxPower = busData?.['template-attribute']?.['battery-capacity'] || 0
    const maxMass = busData?.['template-attribute']?.['mass'] || 0
    const maxSize = busData?.['template-attribute']?.['sizeInUnits'] || 0

    const busMass = busData?.['template-attribute']?.['bus-mass'] || 0
    const busPower = busData?.['template-attribute']?.['bus-power'] || 0
    const busSize = busData?.['template-attribute']?.['bus-size'] || 0

    const payloadPower = maxPower - busPower
    const payloadMass = maxMass - busMass
    const payloadSize = maxSize - busSize

    const default_data = {
        name: busData?.name || '',
        maxPower: String(maxPower) || '',
        maxMass: String(maxMass) || '',
        size: {
            breadth: String(breadth) || '',
            width: String(width) || '',
            height: String(height) || '',
        },
        inertia: busData?.['assembly'][0]['iADCS']['atmos-data']['inertia'],
        maxPayloadSize: String(payloadSize) || '',
        maxPayloadPower: String(payloadPower) || '',
        maxPayloadMass: String(payloadMass) || '',
        simulationParameters: {
            data: JSON.stringify(busData?.['assembly'][0]?.['iADCS']?.['atmos-data']?.['adcs_config']),
            name: `Sample_simulation_parameters.json`
        },
    }
    const updatedTemplate = {
        Core: {
            "adcsType": 'Integrated',
            ...busData?.['assembly']?.[0]?.['core'],
            'iADCS': busData?.['assembly']?.[0]?.['iADCS']?.['atmos-data'] || {},
            'ADCS': busData?.['assembly']?.[0]?.['iADCS']?.['atmos-data'] || {},
            'Bus OBC': busData?.['assembly']?.[0]?.['core']?.['Bus OBC']?.['atmos-data'] || {},
            'Bus OS': busData?.['assembly']?.[0]?.['core']?.['Bus OS']?.['atmos-data'] || {},
            'GPS': busData?.['assembly']?.[0]?.['core']?.['GPS']?.['atmos-data'] || {},
            'Thruster': busData?.['assembly']?.[0]?.['core']?.['Thruster']?.['atmos-data'] || {},
            'Temperature Sensor': busData?.['assembly']?.[0]?.['core']?.['Temperature Sensor']?.['atmos-data'] || {},
            'ThrusterCount': busData?.['assembly']?.[0]?.['core']?.['Thruster']?.['user_data']?.['numUnits'] || 0,
            'Temperature SensorCount': busData?.['assembly']?.[0]?.['core']?.['Temperature Sensor']?.['user_data']?.['numUnits'] || 0
        },
        Payload: {
            'Payload OBC': busData?.['assembly']?.[0]?.['payloadServer']?.['Payload OBC']?.['atmos-data'] || {},
            'Payload SSD': busData?.['assembly']?.[0]?.['payloadServer']?.['Payload SSD']?.['atmos-data'] || {},
            'Payload SSDCount': busData?.['assembly']?.[0]?.['payloadServer']?.['Payload SSD']?.['user_data']?.['numUnits'] || 0
        },
        Comm: {
            'S-Band Antenna': busData?.['assembly']?.[0]?.['communication']?.['antennas']?.['S-band']?.['atmos-data'] || {},
            'X-Band Antenna': busData?.['assembly']?.[0]?.['communication']?.['antennas']?.['X-band']?.['atmos-data'] || {},
            'K-Band Antenna': busData?.['assembly']?.[0]?.['communication']?.['antennas']?.['K-band']?.['atmos-data'] || {},
            'UHF Antenna': busData?.['assembly']?.[0]?.['communication']?.['antennas']?.['UHF']?.['atmos-data'] || {},
            'S-Band Radio': busData?.['assembly']?.[0]?.['communication']?.['comm-devices']?.['S-band']?.['atmos-data'] || {},
            'X-Band Radio': busData?.['assembly']?.[0]?.['communication']?.['comm-devices']?.['X-band']?.['atmos-data'] || {},
            'K-Band Radio': busData?.['assembly']?.[0]?.['communication']?.['comm-devices']?.['K-band']?.['atmos-data'] || {},
            'UHF Radio': busData?.['assembly']?.[0]?.['communication']?.['comm-devices']?.['UHF']?.['atmos-data'] || {}
        },
        Power: {
            'EPS': busData?.['assembly']?.[0]?.['power']?.['EPS']?.['atmos-data'] || {},
            'Battery Pack': busData?.['assembly']?.[0]?.['power']?.['Battery Pack']?.['atmos-data'] || {},
            'Solar Panel': busData?.['assembly']?.[0]?.['power']?.['Solar Panel']?.['atmos-data'] || {},
            'Solar PanelCount': busData?.['assembly']?.[0]?.['power']?.['Solar Panel']?.['user_data']?.['numUnits'] || 0
        }
    }
    return {
        formData: default_data,
        updatedTemplate
    }
}


export const downloadJSON = (data: any, filename = 'data.json') => {
    const jsonStr = JSON.stringify(data, null, 2); // pretty print
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url); // cleanup
}


export const getEditedFormData = (catalog: any, catalogType: string) => {
    const FormData = {} as { [key: string]: any };
    const default_data = {
        name: catalog?.name || '',
        size: String(catalog?.['product-cookie']?.Size || ''),
        mass: String(catalog?.weight_in_gms / 1000) || '',
        peakPower: String(catalog?.max_power_consumption_in_watts || ''),
        averagePower: String(catalog?.average_power_consumption || ''),
    }
    const additionalParams = catalog?.component_specific_attributes?.additionalParams || catalog?.component_specific_attributes?.AdditionalParams || {}
    const typeSpecificParams = catalog?.component_specific_attributes?.typeSpecificParams || {}
    if (catalogType === CatalogConstants.Payload) {
        default_data['voltage'] = String(catalog?.voltage || '')
        default_data['interfaces'] = catalog?.component_specific_attributes?.interfaces || []
        default_data['payloadDetails'] = catalog?.component_specific_attributes?.payloadDetails === 'MX / Multispectral' ? 'Multispectral' : catalog?.component_specific_attributes?.payloadDetails || ''
        default_data['payloadSubType'] = catalog?.component_specific_attributes?.payloadSubType || ''
        default_data['payloadType'] = catalog?.component_specific_attributes?.payloadType || ''
        default_data['typeSpecificParams'] = getRevertedTypeSpecificParams(typeSpecificParams, catalogType) || {}
        default_data['power_sinks'] = getUpdatedPowerSinkData('string', catalog?.power_sinks || [DEFAULT_POWER_SINK_DATA])
        FormData['data'] = default_data
        FormData['additionalConfig'] = Object.entries(additionalParams).map(
            ([k, v]) => ({ [k]: v })
        ) || []
    } else if (catalogType === CatalogConstants.Edge) {
        default_data['interfaces'] = catalog?.component_specific_attributes?.interfaces || []
        default_data['typeSpecificParams'] = getRevertedTypeSpecificParams(catalog?.['product-cookie'], catalogType) || {}
        default_data['power_sinks'] = getUpdatedPowerSinkData('string', catalog?.power_sinks || [DEFAULT_POWER_SINK_DATA])
        FormData['data'] = default_data
        FormData['additionalConfig'] = Object.entries(additionalParams).map(
            ([k, v]) => ({ [k]: v })
        ) || []
    } else {
        default_data['component_specific_attributes'] = catalog?.component_specific_attributes || {}
        default_data['catalogueType'] = catalog?.['catalogue-type'] || ''
        if (catalog?.['catalogue-type'] === 'Payload SSD') {
            default_data['size_in_MB'] = String(catalog?.['size_in_MB'] / 1024)
        }
        if (POWER_SINK_COMPONENTS.includes(catalog?.['catalogue-type']) && catalog?.['catalogue-type'] !== 'iADCS') {
            default_data['power_sinks'] = getUpdatedPowerSinkData('string', catalog?.power_sinks || [DEFAULT_POWER_SINK_DATA])
        }
        FormData['data'] = default_data
        FormData['additionalConfig'] = Object.entries(catalog?.['product-cookie']).filter(([k, v]) => k !== 'Size').map(
            ([k, v]) => ({ [k]: v })
        ) || []
    }
    return FormData
};

export const getLoadingButtonText = (mode: "Create" | "Update" | "Clone" | null) => {
    switch (mode) {
        case "Create":
            return "Creating";
        case "Update":
            return "Updating";
        case "Clone":
            return "Cloning";
        default:
            return "Create";
    }
};


export const handleExportTemplate = (template: any, name: string) => {
    try {
        const jsonData = JSON.stringify(template, null, 2); // Convert JSON data to a string with indentation
        const blob = new Blob([jsonData], { type: 'application/json' }); // Use 'application/json' for JSON data
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = name;
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
    } catch (error) {
        console.error('Error downloading template:', error);
        showToastMessage('error', 'Error downloading template');
    }
}